import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useRoleApi } from '../../services/api/useRole.api';
import { Role } from '../../types/Role/Role.type.ts';
import { Team } from '../../types/Team/Team.type';
import { User } from '../../types/User/User.type.ts';
import { UserUpdate } from '../../types/User/UserUpdate.ts';

interface UserEditModalProps {
  open: boolean;
  onClose: () => void;
  user: User;
  teams: Team[];
  loading: boolean;
  onUpdate: (payload: UserUpdate) => void;
}

function UserEditModal({ open, onClose, user, teams, loading, onUpdate }: UserEditModalProps) {
  const { getRoles } = useRoleApi();

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const currentFirstName = user.first_name ?? '';
  const currentLastName = user.last_name ?? '';
  const currentRoleId = user.role.id;
  const currentTeamId = user.team?.id;

  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [roleId, setRoleId] = useState<number>(currentRoleId);
  const [teamId, setTeamId] = useState<number | undefined>(currentTeamId);

  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();

  const [prevOpen, setPrevOpen] = useState(open);
  if (open && !prevOpen) {
    setPrevOpen(open);
    setFirstName(currentFirstName);
    setLastName(currentLastName);
    setRoleId(currentRoleId);
    setTeamId(currentTeamId);
  }
  if (open !== prevOpen) {
    setPrevOpen(open);
  }

  const hasChanges =
    firstName.trim() !== currentFirstName ||
    lastName.trim() !== currentLastName ||
    roleId !== currentRoleId ||
    teamId !== currentTeamId;

  const handleUpdateClick = () => {
    if (!hasChanges) return;
    onUpdate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      role_id: roleId,
      team_id: teamId,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='xs'
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (e: React.FormEvent) => {
            e.preventDefault();
            handleUpdateClick();
          },
        },
      }}
    >
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Stack sx={{ backgroundColor: '#F7FAFC', padding: 2, borderRadius: 1 }} spacing={0.5}>
            <Typography
              variant='body1'
              sx={{
                color: 'text.main',
                fontWeight: 'medium',
              }}
            >
              {fullName}
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
              }}
            >
              {user.email}
            </Typography>
          </Stack>
          <TextField
            label='First name'
            type='text'
            fullWidth
            variant='outlined'
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
          />
          <TextField
            label='Last name'
            type='text'
            fullWidth
            variant='outlined'
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
          />
          <FormControl fullWidth>
            <InputLabel id='edit-user-role-label' sx={{ fontSize: '14px' }}>
              Role
            </InputLabel>
            <Select
              labelId='edit-user-role-label'
              value={roleId}
              label='Role'
              onChange={(e) => setRoleId(Number(e.target.value))}
            >
              {roles?.map((role: Role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id='edit-user-team-label' sx={{ fontSize: '14px' }}>
              Team
            </InputLabel>
            <Select
              labelId='edit-user-team-label'
              value={teamId ?? ''}
              label='Team'
              onChange={(e) => setTeamId(Number(e.target.value))}
            >
              {teams.map((team: Team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant='contained' disableElevation disabled={loading || !hasChanges} onClick={handleUpdateClick}>
          {loading ? 'Please wait...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserEditModal;
